/* global SideDrawer */

if (!customElements.get("facet-filters")) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener("popstate", this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener(
          "on:breakpoint-change",
          this.breakpointChangeHandler
        );
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === "true";
      this.sortingEnabled = this.dataset.sorting === "true";
      this.form = document.getElementById("facets");
      this.results = document.getElementById("filter-results");
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      this.historyChangeHandler =
        this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener("popstate", this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector(".facets__filters");
        this.activeFilters = this.querySelector(".facets__active-filters");
        this.activeFiltersList = this.querySelector(".active-filters");
        this.activeFiltersHeader = this.querySelector(".active-filters-header");
        this.footer = this.querySelector(".facets__footer");
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll(".js-drawer-sort-by");
        this.desktopSortBy = document.querySelector(".products-toolbar__sort");
      }

      this.priceFilerBtn = this.querySelector("#filter-price-range");
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler =
          this.breakpointChangeHandler ||
          this.handleBreakpointChange.bind(this);
        this.filters.addEventListener(
          "click",
          this.handleFiltersClick.bind(this)
        );
        this.filters.addEventListener(
          "input",
          this.handleFilterChange.bind(this)
        );
        this.filters.addEventListener(
          "change",
          this.handleFilterChange.bind(this)
        );
        window.addEventListener(
          "on:breakpoint-change",
          this.breakpointChangeHandler
        );
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener(
          "change",
          this.handleFilterChange.bind(this)
        );
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute("open", "");
        this.setAttribute("aria-hidden", "false");
        this.removeAttribute("aria-modal");
        this.removeAttribute("role");
      } else {
        this.close();
        this.setAttribute("role", "dialog");
        this.setAttribute("aria-modal", "true");
        this.setAttribute("aria-hidden", "true");
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (
        evt.type === "change" &&
        !(
          evt.target.id?.includes("price-range") ||
          evt.target.id?.includes("sort-by")
        )
      )
        return;

      // Dont reload when typing a price
      if (
        evt.target.id?.includes("price-range") &&
        evt.constructor.name === "InputEvent"
      )
        return;

      if (evt.target.id?.includes("price-range-number")) {
        return;
      }

      if (evt.target.id?.includes("price-range-radio")) {
        evt.target.value.split("-").forEach(function (item, i) {
          if (i == 0) {
            document.querySelector('[name="filter.v.price.gte"]').value = item;
          } else {
            document.querySelector('[name="filter.v.price.lte"]').value = item;
          }
        });
      }

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get("sort_by");

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === "CUSTOM-SELECT") {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set("sort_by", currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches(".js-clear-filter")) {
        evt.preventDefault();
        this.applyFilters(
          new URL(evt.target.href).searchParams.toString(),
          evt
        );
      }

      // Filter 'show more' button clicked.
      if (target.matches(".js-show-more")) {
        const filter = target.closest(".filter");
        target.remove();

        filter.querySelectorAll("li").forEach((el) => {
          el.classList.remove("js-hidden");
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== "A") return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = "";

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector("custom-pagination");
        if (customPagination)
          customPagination.dataset.pauseInfiniteScroll = "true";

        // Set loading state.
        this.results.classList.add("is-loading");

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector(".js-close-drawer-mob");
        closeBtn.ariaDisabled = "true";
        closeBtn.classList.add("is-loading");

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort("Request changed");
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: "GET",
          signal: this.applyFiltersFetchAbortController.signal,
        });

        if (response.ok) {
          const tmpl = document.createElement("template");
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form
            .querySelectorAll("details-disclosure > details")
            .forEach((existingFilter) => {
              const target = tmpl.content.getElementById(existingFilter.id);
              if (target) {
                target.open = existingFilter.open;
              }
            });
          tmpl.content
            .querySelectorAll("#facets details-disclosure > details")
            .forEach((newFilter) => {
              if (this.expanded.includes(newFilter.id)) {
                const hiddenElements = newFilter.querySelectorAll(".js-hidden");
                hiddenElements.forEach((listItem) => {
                  listItem.classList.remove("js-hidden");
                });
                newFilter.querySelector(".filter__more")?.remove();
              }
            });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById("facets").innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector(
            ".js-close-drawer-mob"
          ).innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector("ul");
          this.currentResultsClass = currentResultsUl
            ? this.results.querySelector("ul").getAttribute("class")
            : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML =
            tmpl.content.getElementById("filter-results").innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector("ul");
          if (newResultsUl && this.currentResultsClass)
            newResultsUl.setAttribute("class", this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload)
            customPagination.reload();

          // 重新初始化移动端 filter-tab 切换功能
          if (typeof initMobileFilterTab === "function") {
            initMobileFilterTab();
          }

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight =
                Number.parseInt(
                  getComputedStyle(this.parentElement)
                    .getPropertyValue("--header-height")
                    .replace("px", ""),
                  10
                ) || 0;
            }
            window.scrollTo({
              top:
                this.results.getBoundingClientRect().top +
                window.scrollY -
                headerHeight -
                45,
              behavior: "smooth",
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove("is-loading");
          closeBtn.removeAttribute("aria-disabled");

          // Renable infinite scroll
          if (customPagination)
            customPagination.dataset.pauseInfiniteScroll = "false";

          // Focus on the element with the same ID in the new HTML
          if (activeElementId)
            document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent("on:facet-filters:updated"), {
            bubbles: true,
          });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove("is-loading");
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        "",
        `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
      );
    }
  }

  customElements.define("facet-filters", FacetFilters);
}

function isMobile() {
  return window.innerWidth < 768 || document.documentElement.clientWidth < 768;
}

let currentFilterTabIndex = null;

function showFilterGroup(index) {
  if (!isMobile()) return;
  var popup = document.querySelector(".filter-group");
  if (popup && !popup.classList.contains("active")) {
    popup.classList.add("active");
  }
  // 只显示对应index的filter-disclosure
  document
    .querySelectorAll(".filter-group .filter-disclosure")
    .forEach((el, i) => {
      if (Number(el.dataset.index) === index) {
        el.style.display = "";
      } else {
        el.style.display = "none";
      }
    });
  setTimeout(() => {
    document.addEventListener("click", onDocumentClick);
  }, 0);
}

function hideFilterGroup() {
  var popup = document.querySelector(".filter-group");
  if (popup && popup.classList.contains("active")) {
    popup.classList.remove("active");
    document
      .querySelectorAll(".filter-group .filter-disclosure")
      .forEach((el) => {
        el.style.display = "none";
      });
    document
      .querySelectorAll(".filter-tab-item")
      .forEach((t) => t.removeAttribute("open"));
    document.removeEventListener("click", onDocumentClick);
    currentFilterTabIndex = null;
  }
}

function onDocumentClick(e) {
  var popup = document.querySelector(".filter-group");
  if (!popup) return;
  if (!popup.contains(e.target)) {
    hideFilterGroup();
  }
}

function bindFilterGroupEvents() {
  // 先解绑所有事件，防止重复绑定
  document.querySelectorAll(".filter-tab-item").forEach((tab) => {
    tab.onclick = null;
  });
  document.querySelectorAll(".filter-group .filter__label").forEach((label) => {
    label.onclick = null;
  });
  var group = document.querySelector(".filter-group");
  if (group) group.onclick = null;
  document.removeEventListener("click", onDocumentClick);

  // 阻止弹层内部点击冒泡到 document
  if (group) {
    group.onclick = function (e) {
      e.stopPropagation();
    };
  }

  // 绑定 tab 显示弹层，连续点击同tab时切换显示/隐藏
  document.querySelectorAll(".filter-tab-item").forEach((tab, idx) => {
    tab.onclick = function (e) {
      e.stopPropagation();
      const popup = document.querySelector(".filter-group");
      // 点击同一个tab时关闭弹窗
      if (
        popup &&
        popup.classList.contains("active") &&
        currentFilterTabIndex === idx
      ) {
        hideFilterGroup();
        return;
      }
      // 切换tab时，如果弹窗未打开，先打开
      if (!popup.classList.contains("active")) {
        currentFilterTabIndex = idx;
        showFilterGroup(idx);
      } else {
        // 弹窗已打开，切换内容但不关闭
        currentFilterTabIndex = idx;
        document
          .querySelectorAll(".filter-group .filter-disclosure")
          .forEach((el, i) => {
            if (Number(el.dataset.index) === idx) {
              el.style.display = "";
            } else {
              el.style.display = "none";
            }
          });
      }

      document.querySelectorAll(".filter-tab-item").forEach((t, i) => {
        if (i === idx) {
          t.setAttribute("open", "");
        } else {
          t.removeAttribute("open");
        }
      });
    };
  });

  // 选项点击后隐藏
  document.querySelectorAll(".filter-group .filter__label").forEach((label) => {
    label.onclick = function () {
      hideFilterGroup();
    };
  });
}

// 页面加载和每次筛选区域更新后都要重新绑定
window.addEventListener("DOMContentLoaded", bindFilterGroupEvents);
document.addEventListener("on:facet-filters:updated", function () {
  bindFilterGroupEvents();
});

function showAllFilterDisclosures() {
  // 显示所有 filter-disclosure
  document
    .querySelectorAll(".filter-group .filter-disclosure")
    .forEach((el) => {
      el.style.display = "";
    });
  // 清除所有 tab 的点击事件
  document.querySelectorAll(".filter-tab-item").forEach((tab) => {
    tab.onclick = null;
  });
  // 清除 filter-group 的点击事件和 active 类
  var group = document.querySelector(".filter-group");
  if (group) {
    group.onclick = null;
    group.classList.remove("active");
  }
  // 移除 document 上的 click 事件监听
  document.removeEventListener("click", onDocumentClick);
  // 重置当前tab索引
  currentFilterTabIndex = null;
}

// 在断点切换到PC端时调用
window.addEventListener("on:breakpoint-change", function () {
  if (!isMobile()) {
    showAllFilterDisclosures();
  } else {
    // 重新绑定移动端事件
    bindFilterGroupEvents();
  }
});
